import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ComposedChart, LabelList
} from 'recharts';

const COLORS = {
  ww: '#1a8a3f',
  coles: '#e03131',
  ty: '#3b82f6',
  ly: 'var(--text-muted)',
  budget: 'var(--text-muted)',
  forecast: '#f59e0b',
  estimate: '#a855f7',
  high: '#22c55e',
  moderate: '#f59e0b',
  low: 'var(--text-muted)',
};

function fmt(val) {
  if (val == null) return '';
  if (Math.abs(val) >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (Math.abs(val) >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${Math.round(val)}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '10px 14px' }}>
      <div style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 6, fontSize: 12 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          <span style={{ color: 'var(--text-body)', fontSize: 12 }}>{p.name}:</span>
          <span style={{ color: 'var(--text-primary)', fontSize: 12 }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

function DeltaLabel({ x, y, width, height, value }) {
  if (value == null || !isFinite(value)) return null;
  const color = value >= 0 ? 'var(--success)' : 'var(--danger)';
  const sign = value >= 0 ? '+' : '';
  return (
    <text x={x + width + 6} y={y + height / 2} fill={color} fontSize={10} dominantBaseline="middle">
      {sign}{value.toFixed(1)}%
    </text>
  );
}

export function CustomerBarChart({ data }) {
  const chartData = data
    .filter(d => (d.gsv_ty || 0) + (d.gsv_ly || 0) > 0)
    .map(d => {
      const ty = Math.round(d.gsv_ty || 0);
      const ly = Math.round(d.gsv_ly || 0);
      const delta = ly > 0 ? ((ty - ly) / ly * 100) : null;
      return {
        name: d.customer.replace(' Limited', '').replace(' Supermarkets', '').replace(' Australia', '').replace(' Wholesale', '').replace(' Trading Ltd Australia', ''),
        'YTD TY': ty,
        'YTD LY': ly,
        delta,
      };
    });

  const rowHeight = 28;
  const chartHeight = chartData.length * rowHeight + 8;

  return (
    <div style={{ overflowY: 'auto', maxHeight: 320 }}>
      <div style={{ height: chartHeight, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 60, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
            <XAxis type="number" tickFormatter={v => `$${(v/1e6).toFixed(1)}M`} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={120} tick={{ fill: 'var(--text-primary)', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff08' }} />
            <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-body)' }} />
            <Bar dataKey="YTD LY" fill={COLORS.ly} radius={[0, 3, 3, 0]} barSize={10} />
            <Bar dataKey="YTD TY" fill={COLORS.ty} radius={[0, 3, 3, 0]} barSize={10}>
              <LabelList dataKey="delta" content={<DeltaLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function PeriodTrendChart({ data }) {
  // Draw the "← Actual" boundary at the last period that has TY data, so
  // the chart auto-advances as new months' actuals land.
  const withActuals = (data || []).filter(p => p.gsv_ty != null);
  const lastActualLabel = withActuals.length ? withActuals[withActuals.length - 1].label : null;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart data={data} margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={v => `$${(v/1e6).toFixed(1)}M`} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff06' }} />
        <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-body)' }} />
        <Bar dataKey="budget_gsv" name="Budget" fill={COLORS.budget} opacity={0.6} radius={[3, 3, 0, 0]} />
        <Line dataKey="gsv_ly" name="LY" stroke={COLORS.ly} strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />
        <Line dataKey="forecast_gsv" name="Forecast" stroke={COLORS.forecast} strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />
        <Line dataKey="gsv_ty" name="Actual" stroke={COLORS.ty} strokeWidth={2} dot={{ fill: COLORS.ty, r: 3 }} connectNulls />
        {lastActualLabel && (
          <ReferenceLine x={lastActualLabel} stroke="#3b82f6" strokeDasharray="4 2" label={{ value: '← Actual', fill: '#3b82f6', fontSize: 10 }} />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function OpportunityChart({ data }) {
  const byType = {};
  data.forEach(d => {
    if (!byType[d.type]) byType[d.type] = { type: d.type, High: 0, Moderate: 0, Low: 0 };
    const prob = d.probability.includes('High') ? 'High' : d.probability.includes('Moderate') ? 'Moderate' : 'Low';
    if (d.value > 0) byType[d.type][prob] = (byType[d.type][prob] || 0) + d.value;
  });

  const chartData = Object.values(byType).sort((a, b) => (b.High + b.Moderate + b.Low) - (a.High + a.Moderate + a.Low));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={chartData} margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="type" tick={{ fill: 'var(--text-body)', fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={v => `$${(v/1e6).toFixed(0)}M`} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff06' }} />
        <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-body)' }} />
        <Bar dataKey="High" stackId="a" fill={COLORS.high} />
        <Bar dataKey="Moderate" stackId="a" fill={COLORS.moderate} />
        <Bar dataKey="Low" stackId="a" fill={COLORS.low} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

const AVG_PRICE = { Woolworths: 7.50, Coles: 7.80 };

export function WeeklySalesChart({ data, retailer, weeks = 13, metric = 'RSV' }) {
  const price = AVG_PRICE[retailer] || 7.50;
  const conv = v => v != null ? v / price : null;
  const isWW = retailer === 'Woolworths';

  const filtered = data.slice(-weeks).map(d => {
    if (metric === 'Units') {
      const tyUnits = isWW ? d.ww_units_ty : d.coles_units_ty;
      const lyUnits = isWW ? d.ww_units_ly : d.coles_units_ly;
      return {
        date: d.date_ty.slice(5, 10),
        TY: tyUnits ?? conv(isWW ? d.ww_ty : d.coles_ty),
        LY: lyUnits ?? conv(isWW ? d.ww_ly : d.coles_ly),
      };
    }
    return {
      date: d.date_ty.slice(5, 10),
      TY: isWW ? d.ww_ty : d.coles_ty,
      LY: isWW ? d.ww_ly : d.coles_ly,
    };
  });

  const color = retailer === 'Woolworths' ? COLORS.ww : COLORS.coles;
  const tickFmt = metric === 'Units'
    ? v => `${(v/1e3).toFixed(0)}K`
    : v => `$${(v/1e3).toFixed(0)}K`;
  const tipFmt = metric === 'Units'
    ? v => `${(v/1e3).toFixed(1)}K u`
    : v => `$${(v/1e3).toFixed(0)}K`;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={filtered} margin={{ left: 4, right: 8, top: 4, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} interval={2} />
        <YAxis tickFormatter={tickFmt} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} width={44} />
        <Tooltip formatter={(v, n) => [tipFmt(v), n]} contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 12 }} cursor={{ fill: '#ffffff06' }} />
        <Legend wrapperStyle={{ fontSize: 11, color: 'var(--text-body)', paddingTop: 4 }} />
        <Area dataKey="LY" name="LY" fill={color} fillOpacity={0.06} stroke={COLORS.ly} strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
        <Line dataKey="TY" name="TY" stroke={color} strokeWidth={2.5} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function MarketShareChart({ data, retailer, weeks = 13 }) {
  const filtered = data.slice(-weeks).map(d => {
    const sales = retailer === 'Woolworths' ? d.ww_ty : d.coles_ty;
    const cat = retailer === 'Woolworths' ? d.ww_cat : d.coles_cat;
    const salesLY = retailer === 'Woolworths' ? d.ww_ly : d.coles_ly;
    return {
      date: d.date_ty.slice(5, 10),
      shareTY: cat ? (sales / cat) * 100 : null,
      shareLY: cat ? (salesLY / cat) * 100 : null,
    };
  });

  const color = retailer === 'Woolworths' ? COLORS.ww : COLORS.coles;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={filtered} margin={{ left: 4, right: 8, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} interval={2} />
        <YAxis tickFormatter={v => `${v.toFixed(1)}%`} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
        <Tooltip formatter={(v) => `${v?.toFixed(2)}%`} contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8 }} />
        <Area dataKey="shareLY" name="LY Share" fill="none" stroke={COLORS.ly} strokeWidth={1} strokeDasharray="4 3" dot={false} />
        <Area dataKey="shareTY" name="TY Share" fill={color} fillOpacity={0.15} stroke={color} strokeWidth={2} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}
